/**
 * Produits & solutions — le catalogue de ce que l'équipe vend.
 *
 * Remplace « Notre offre », qui ne décrivait qu'une solution unique : la même
 * pour un restaurateur et pour un cabinet comptable. Ici chaque solution porte
 * son propre discours et les activités qu'elle vise ; l'administrateur décide
 * de ce rattachement. Sur une fiche, le CRM propose la solution adaptée à
 * l'activité de l'entreprise, et le discours d'appel comme le copilote
 * s'alignent dessus.
 *
 * L'identité du vendeur (qui appelle) reste unique et vit en tête de page.
 * Lecture pour toute l'équipe — un commercial doit connaître son catalogue —,
 * écriture réservée à l'administrateur.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Package, Plus, Pencil, Trash2, Power, Building2, Save, RefreshCw, Layers, Trash,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import { Loader } from "@/crm/ui";
import Modal from "@/crm/components/Modal";
import SolutionForm from "@/crm/components/solutions/SolutionForm";

const CARD = "bg-white rounded-2xl border border-slate-200";
const FIELD = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-500 disabled:bg-slate-50 disabled:text-slate-500";

const IDENTITY_FIELDS = [
  ["entreprise", "Nom de votre entreprise", "Prospee IT", "Le nom prononcé à l'ouverture de l'appel."],
  ["ton", "Ton attendu", "professionnel, direct, chaleureux", ""],
  ["site", "Site web", "prospee.fr", ""],
  ["telephone", "Téléphone", "01 23 45 67 89", ""],
  ["email", "Email", "contact@prospee.fr", ""],
];

export default function Solutions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Les solutions retirées du catalogue ne concernent que ceux qui le gèrent.
  const seeInactive = isAdmin || user?.role === "manager";
  const [items, setItems] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | solution
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: id }] = await Promise.all([
        api.get(seeInactive ? "/solutions?all=true" : "/solutions"),
        api.get("/solutions/identite"),
      ]);
      setItems(list);
      setIdentity(id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Catalogue indisponible");
      setItems([]);
    }
  }, [seeInactive]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    setItems((list) => list.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x))); // optimiste
    try { await api.put(`/solutions/${s.id}`, { active: !s.active }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    load();
  };

  const remove = async () => {
    const s = confirm;
    setConfirm(null);
    try { await api.delete(`/solutions/${s.id}`); toast.success("Solution supprimée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loader />;

  return (
    <div className="max-w-[1320px] mx-auto">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[.14em] text-slate-400">Catalogue</div>
          <h1 className="font-heading text-[30px] font-bold tracking-[-.02em] text-slate-900">Produits & solutions</h1>
          <p className="mt-2 max-w-[60ch] text-[14.5px] text-slate-500 text-pretty">
            Ce que l'équipe vend, et à quelles activités chaque solution s'adresse. Sur une fiche,
            le discours d'appel et le copilote s'adaptent à la solution retenue.
          </p>
        </div>
        {isAdmin && (
          <button data-testid="new-solution-btn" onClick={() => setEditing({})}
            className="flex items-center gap-2.5 rounded-[11px] bg-indigo-600 px-[18px] py-[11px] text-sm font-medium text-white shadow-[0_6px_18px_rgba(74,58,224,.24)] hover:bg-indigo-700">
            <Plus size={16} /> Nouvelle solution
          </button>
        )}
      </div>

      <IdentityCard identity={identity} editable={isAdmin} onSaved={setIdentity} />

      {items.length === 0 ? (
        <div className={`${CARD} px-6 py-[54px] text-center`} data-testid="solutions-empty">
          <div className="mx-auto mb-3.5 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50">
            <Package size={20} className="text-indigo-600" />
          </div>
          <div className="font-heading text-base font-bold text-slate-900">Aucune solution au catalogue</div>
          <p className="mt-[7px] text-[13.5px] text-slate-500">
            {isAdmin
              ? "Ajoutez ce que vous vendez : chaque solution porte son discours et les activités qu'elle vise."
              : "L'administrateur n'a pas encore renseigné le catalogue."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-[14px] md:grid-cols-2 xl:grid-cols-3">
          {items.map((s) => (
            <SolutionCard key={s.id} solution={s} isAdmin={isAdmin}
              onEdit={() => setEditing(s)} onToggle={() => toggle(s)} onDelete={() => setConfirm(s)} />
          ))}
        </div>
      )}

      {editing !== null && (
        <SolutionForm solution={editing.id ? editing : null} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {confirm && <ConfirmDelete name={confirm.nom} onClose={() => setConfirm(null)} onConfirm={remove} />}
    </div>
  );
}

/** Qui appelle : unique, joint à chaque discours quelle que soit la solution. */
function IdentityCard({ identity, editable, onSaved }) {
  const [f, setF] = useState(identity || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(identity || {}); }, [identity]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/solutions/identite", f);
      onSaved(data);
      toast.success("Identité enregistrée — les prochains discours en tiendront compte");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const dirty = editable && identity && IDENTITY_FIELDS.some(([k]) => (f[k] || "") !== (identity[k] || ""));

  return (
    <div className={`${CARD} mb-4 p-5`} data-testid="seller-identity">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Building2 size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-[15.5px] font-bold text-slate-900">Qui appelle</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">
            L'identité du vendeur est jointe à chaque discours, quelle que soit la solution proposée
            (variables <span className="font-mono text-[12px]">{"{{nous.entreprise}}"}</span>,{" "}
            <span className="font-mono text-[12px]">{"{{nous.ton}}"}</span>…).
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {IDENTITY_FIELDS.map(([key, label, placeholder, hint]) => (
          <label key={key} className={`block text-[12.5px] font-medium text-slate-600 ${key === "entreprise" || key === "ton" ? "lg:col-span-2" : ""}`}>
            {label}
            <input data-testid={`identity-${key}`} value={f[key] || ""} placeholder={editable ? placeholder : "—"}
              disabled={!editable} onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))}
              className={`${FIELD} mt-1`} />
            {hint && <span className="mt-1 block text-[11.5px] font-normal text-slate-400">{hint}</span>}
          </label>
        ))}
      </div>
      {editable && (
        <button data-testid="identity-save" onClick={save} disabled={saving || !dirty}
          className="mt-4 flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
          {saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
          Enregistrer l'identité
        </button>
      )}
    </div>
  );
}

function SolutionCard({ solution: s, isAdmin, onEdit, onToggle, onDelete }) {
  const activities = s.activites_labels || [];
  return (
    <div data-testid="solution-card"
      className={`${CARD} flex flex-col gap-3 p-[18px] ${s.active ? "" : "opacity-[.62]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-[15.5px] font-bold text-slate-900">{s.nom}</span>
            <span className={`rounded-full px-2 py-[3px] font-mono text-[10.5px] uppercase tracking-[.06em] ${
              s.type === "produit" ? "bg-sky-50 text-sky-700" : "bg-indigo-50 text-indigo-700"}`}>
              {s.type === "produit" ? "produit" : "solution"}
            </span>
            {!s.active && (
              <span className="rounded-full bg-slate-100 px-2 py-[3px] font-mono text-[10.5px] uppercase tracking-[.06em] text-slate-500">retirée</span>
            )}
          </div>
          {s.proposition_valeur && <p className="mt-1 text-[13.5px] text-slate-700 text-pretty">{s.proposition_valeur}</p>}
        </div>
        {isAdmin && (
          <div className="flex shrink-0 items-center gap-1">
            <button data-testid="toggle-solution-btn" onClick={onToggle} title={s.active ? "Retirer du catalogue" : "Remettre au catalogue"}
              className={`rounded-[9px] p-1.5 hover:bg-slate-100 ${s.active ? "text-emerald-600" : "text-slate-300"}`}><Power size={16} /></button>
            <button data-testid="edit-solution-btn" onClick={onEdit} title="Modifier"
              className="rounded-[9px] p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil size={16} /></button>
            <button data-testid="delete-solution-btn" onClick={onDelete} title="Supprimer"
              className="rounded-[9px] p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      {s.contexte && (
        <p className="text-[13px] text-slate-500 text-pretty">
          <span className="font-medium text-slate-600">Quand la proposer · </span>{s.contexte}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <Layers size={13} className="text-slate-400" />
        {activities.length === 0 ? (
          <span className="text-[12px] text-slate-500">Toutes activités</span>
        ) : activities.map((a) => (
          <span key={a} className="rounded-md bg-slate-100 px-[7px] py-[3px] text-[11.5px] text-slate-600">{a}</span>
        ))}
      </div>

      {(s.arguments?.length > 0 || s.preuves?.length > 0 || s.prix) && (
        <div className="mt-auto flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-100 pt-2.5 font-mono text-[11.5px] text-slate-400">
          {s.arguments?.length > 0 && <span>{s.arguments.length} argument{s.arguments.length > 1 ? "s" : ""}</span>}
          {s.preuves?.length > 0 && <span>{s.preuves.length} preuve{s.preuves.length > 1 ? "s" : ""}</span>}
          {s.prix && <span className="text-slate-600">{s.prix}</span>}
        </div>
      )}
    </div>
  );
}

function ConfirmDelete({ name, onClose, onConfirm }) {
  return (
    <Modal onClose={onClose} zClassName="z-[70]" testid="solution-delete-modal" label="Supprimer la solution"
      className="max-w-[440px] rounded-[18px]">
      <div className="flex items-start gap-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-rose-50">
          <Trash size={19} className="text-rose-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-[17.5px] font-bold text-slate-900">Supprimer la solution</h3>
          <p className="mt-2 text-sm text-slate-500 text-pretty">
            « {name} » disparaît du catalogue. Les fiches qui la retenaient repassent en choix automatique ;
            les discours déjà générés sont conservés.
          </p>
        </div>
      </div>
      <div className="mt-[22px] flex flex-wrap justify-end gap-2.5">
        <button onClick={onClose} className="rounded-[11px] border border-slate-200 bg-white px-4 py-[11px] text-sm text-slate-600 hover:border-slate-300">Annuler</button>
        <button data-testid="solution-delete-confirm" onClick={onConfirm}
          className="rounded-[11px] bg-rose-600 px-[18px] py-[11px] text-sm font-medium text-white hover:bg-rose-700">Supprimer</button>
      </div>
    </Modal>
  );
}
